/**
 * [#663] PortalShellLayout — the Tenant Lead Portal section of the EXISTING
 * shell.
 *
 * Same NuShell frame, same rail, same 52px toolbar, same tokens as every
 * other authenticated screen (design brief §2 forbids a second visual
 * language). What differs is the nav axis and the identity slot:
 *
 *  - the rail renders the portal section only, filtered by the REAL per-tenant
 *    page-grant matrix read from the server (`GET /api/v1/portal/me`). A
 *    tenant granted only Deliveries sees only Deliveries;
 *  - the toolbar carries the tenant's own identity (name + logo slot), a
 *    tenant-isolation cue required by brief §5. There is no tenant switcher,
 *    no other tenant's name, and no cross-tenant affordance anywhere;
 *  - the shell footer keeps the ordinary account entries (role-filtered by
 *    the existing `visibleNavForRole`) so the portal is a section of the app,
 *    not an island.
 *
 * Grant filtering here is VISIBILITY. Direct navigation to an ungranted area
 * renders the designed permission-denied state, and the server refuses the
 * area's data regardless (`requirePortalGrant`) — hidden nav is never the
 * boundary.
 */

import React, { useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { usePortalSession } from "../../context/PortalSessionContext";
import {
  PortalDeniedState,
  PortalErrorState,
  PortalLoadingState,
} from "../portal/PortalStates";
import { NuShell } from "./NuShell";
import { visibleNavForRole } from "./navConfig";
import {
  hasGrant,
  pageAreaForRoute,
  portalNavForGrants,
  PORTAL_NAV_ITEMS,
  grantedPageAreas,
} from "./portalNavConfig";

/** Tenant identity chip for the toolbar — the tenant's OWN name only. */
export const PortalTenantIdentity: React.FC<{ tenantName: string | null }> = ({
  tenantName,
}) => (
  <span
    data-testid="portal-tenant-identity"
    className="nu-pill nu-pill-primary"
    style={{ marginLeft: 12, maxWidth: 280, overflow: "hidden" }}
  >
    {/* Logo slot: tenant logos are not part of the shipped design pack, so
        the slot renders the tenant's initial rather than a fabricated asset. */}
    <span
      aria-hidden="true"
      data-testid="portal-tenant-logo-slot"
      style={{
        width: 16,
        height: 16,
        borderRadius: 4,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--nu-surface)",
        color: "var(--nu-primary)",
        font: "600 10px/1 var(--nu-font-sans)",
        flexShrink: 0,
      }}
    >
      {(tenantName ?? "?").trim().charAt(0).toUpperCase() || "?"}
    </span>
    <span
      style={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {tenantName ?? "Your organisation"}
    </span>
  </span>
);

const PortalShellLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthContext();
  const { darkMode, toggleDarkMode } = useTheme();
  const theme = darkMode ? "dark" : "light";
  const { status, tenantName, grants, deniedReason, errorMessage, reload } =
    usePortalSession();

  const navGroups = useMemo(() => portalNavForGrants(grants), [grants]);
  const navFooter = useMemo(
    () => visibleNavForRole(user?.role).footer,
    [user?.role],
  );

  const requestedArea = pageAreaForRoute(location.pathname);
  const activeItem = PORTAL_NAV_ITEMS.find(
    (item) => item.pageArea === requestedArea,
  );

  const availableLinks = useMemo(
    () =>
      grantedPageAreas(grants).map((area) => {
        const item = PORTAL_NAV_ITEMS.find((i) => i.pageArea === area);
        return { label: item?.label ?? area, route: item?.route ?? "/" };
      }),
    [grants],
  );

  let content: React.ReactNode;
  if (status === "loading") {
    content = <PortalLoadingState />;
  } else if (status === "error") {
    content = <PortalErrorState message={errorMessage} onRetry={reload} />;
  } else if (status === "denied") {
    // [#698] The server refused the SESSION itself. `/portal/me` carries no
    // page grant, so this is the role refusal — and it must not borrow the
    // grant wording: a user whose role is unratified would otherwise be told
    // their organisation lacks a grant and go ask NU GUI for one that would
    // not help. The reason comes from the server's envelope; role_denied is
    // the fallback because it is the only reason that endpoint emits.
    content = (
      <PortalDeniedState
        reason={deniedReason ?? "role_denied"}
        onNavigate={(route) => navigate(route)}
      />
    );
  } else if (requestedArea === null || !hasGrant(grants, requestedArea)) {
    // Direct navigation to an area this tenant does not hold — the designed
    // denied state, reached without ever asking the server for its data.
    content = (
      <PortalDeniedState
        reason="grant_denied"
        areaLabel={activeItem?.label}
        availableLinks={availableLinks}
        onNavigate={(route) => navigate(route)}
      />
    );
  } else {
    content = <Outlet />;
  }

  return (
    <NuShell
      route={location.pathname}
      onNavigate={(route) => navigate(route)}
      theme={theme}
      onToggleTheme={toggleDarkMode}
      navGroups={navGroups}
      navFooter={navFooter}
      title={activeItem?.label ?? "Lead Portal"}
      identity={<PortalTenantIdentity tenantName={tenantName} />}
      user={
        user
          ? { name: user.username, email: user.email, role: user.role }
          : undefined
      }
      onLogout={logout}
    >
      {content}
    </NuShell>
  );
};

export default PortalShellLayout;
